/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: sms/pages/SmsOptoutsPage.tsx
 🗓️ Created: 2026-01-20
 🧩 Description:
    SMS Opt-outs management page - following Extensions module pattern.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo, useEffect } from 'react';
import { UserX, Plus, Trash2, RefreshCw } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import DataGridSimple from '../../../components/ui/DataGridSimple';
import { useNotifications } from '../../../contexts/NotificationContext';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { fetchOptouts, addOptout, removeOptout } from '../api';
import type { SmsOptout } from '../types';

interface OptoutColumn {
    key: string;
    label: string;
    sortable: boolean;
    visible: boolean;
    render?: (value: unknown, item: SmsOptout) => React.ReactNode;
}

const REASON_COLORS: Record<string, string> = {
    STOP: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    UNSUBSCRIBE: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    BLOCKED: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400',
    BOUNCED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
};

export default function SmsOptoutsPage() {
    const { t, i18n } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const [data, setData] = useState<SmsOptout[]>([]);
    const [loading, setLoading] = useState(true);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [newPhoneNumber, setNewPhoneNumber] = useState('');
    const [newReason, setNewReason] = useState('STOP');
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; optout: SmsOptout | null }>({
        isOpen: false,
        optout: null,
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);

    const formatDate = (date: string) => new Date(date).toLocaleString();

    // Define columns
    const availableColumns = useMemo<OptoutColumn[]>(() => [
        {
            key: 'phoneNumber',
            label: t('sms.optouts.columns.phoneNumber'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsOptout) => (
                <span className="font-mono font-medium text-primary">{item?.phoneNumber || '-'}</span>
            ),
        },
        {
            key: 'reason',
            label: t('sms.optouts.columns.reason'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsOptout) => (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${REASON_COLORS[item?.reason] || ''}`}>
                    {item?.reason || '-'}
                </span>
            ),
        },
        {
            key: 'createdAt',
            label: t('sms.optouts.columns.date'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsOptout) => formatDate(item?.createdAt),
        },
        {
            key: 'actions',
            label: t('common.actions'),
            sortable: false,
            visible: true,
        },
    ], [t, i18n.language]);

    const [columns, setColumns] = useState<OptoutColumn[]>(availableColumns);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const optouts = await fetchOptouts();
            setData(optouts);
        } catch (error) {
            showError(error instanceof Error ? error.message : t('sms.optouts.errorLoading'));
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [showError, t]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Filter and sort data
    const filteredData = useMemo(() => {
        let result = [...data];

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(item =>
                item.phoneNumber.toLowerCase().includes(query) ||
                item.reason.toLowerCase().includes(query)
            );
        }

        result.sort((a, b) => {
            const aVal = (a as unknown as Record<string, unknown>)[sortBy] ?? '';
            const bVal = (b as unknown as Record<string, unknown>)[sortBy] ?? '';

            if (sortBy === 'createdAt') {
                const cmp = new Date(String(aVal)).getTime() - new Date(String(bVal)).getTime();
                return sortOrder === 'asc' ? cmp : -cmp;
            }

            if (typeof aVal === 'string' && typeof bVal === 'string') {
                const cmp = aVal.localeCompare(bVal);
                return sortOrder === 'asc' ? cmp : -cmp;
            }

            const aNum = typeof aVal === 'number' ? aVal : 0;
            const bNum = typeof bVal === 'number' ? bVal : 0;
            return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
        });

        return result;
    }, [data, searchQuery, sortBy, sortOrder]);

    const paginatedData = useMemo(() => {
        const start = (page - 1) * limit;
        return filteredData.slice(start, start + limit);
    }, [filteredData, page, limit]);

    const pagination = useMemo(() => ({
        page,
        limit,
        total: filteredData.length,
        totalPages: Math.ceil(filteredData.length / limit),
    }), [page, limit, filteredData.length]);

    const handleSearch = useCallback((query: string) => {
        setSearchQuery(query);
        setPage(1);
    }, []);

    const handleSort = useCallback((key: string) => {
        if (sortBy === key) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortOrder('desc');
        }
    }, [sortBy]);

    const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
    const handleItemsPerPageChange = useCallback((newLimit: number) => {
        setLimit(newLimit);
        setPage(1);
    }, []);

    const handleColumnToggle = useCallback((key: string) => {
        setColumns(prev => prev.map(col =>
            col.key === key ? { ...col, visible: !col.visible } : col
        ));
    }, []);

    const handleAddOpen = useCallback(() => {
        setNewPhoneNumber('');
        setNewReason('STOP');
        setAddModalOpen(true);
    }, []);

    const handleAddSubmit = useCallback(async () => {
        if (!newPhoneNumber.trim()) {
            showError(t('sms.optouts.errorPhoneRequired'));
            return;
        }

        try {
            await addOptout(newPhoneNumber, newReason);
            showSuccess(t('sms.optouts.messages.addSuccess'));
            setAddModalOpen(false);
            loadData();
        } catch (error) {
            showError(error instanceof Error ? error.message : t('sms.optouts.messages.addError'));
        }
    }, [newPhoneNumber, newReason, loadData, showError, showSuccess, t]);

    const handleDelete = useCallback((optout: SmsOptout) => {
        setDeleteModal({ isOpen: true, optout });
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!deleteModal.optout) return;

        try {
            const idToUse = deleteModal.optout.uuid ?? deleteModal.optout.id;
            await removeOptout(idToUse);
            showSuccess(t('sms.optouts.messages.deleteSuccess'));
            setDeleteModal({ isOpen: false, optout: null });
            loadData();
        } catch (error) {
            showError(error instanceof Error ? error.message : t('sms.optouts.messages.deleteError'));
        }
    }, [deleteModal.optout, loadData, showError, showSuccess, t]);

    const renderActions = useCallback((optout: SmsOptout) => (
        <div className="flex items-center gap-2">
            <SimpleTooltip content={t('common.delete')} position="top">
                <button
                    onClick={() => handleDelete(optout)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                </button>
            </SimpleTooltip>
        </div>
    ), [t, handleDelete]);

    return (
        <div className="p-6">
            <DataGridSimple
                data={paginatedData}
                loading={loading}
                columns={columns}
                pagination={pagination}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSearch={handleSearch}
                onSort={handleSort}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                onColumnToggle={handleColumnToggle}
                renderActions={renderActions}
                searchPlaceholder={t('sms.optouts.searchPlaceholder')}
                title={t('sms.optouts.title')}
                titleIcon={<UserX className="h-5 w-5 text-primary" />}
                actions={
                    <div className="flex items-center gap-2">
                        <SimpleTooltip content={t('common.refresh')} position="top">
                            <button
                                onClick={loadData}
                                disabled={loading}
                                className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-foreground rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                            >
                                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                        </SimpleTooltip>
                        <SimpleTooltip content={t('sms.optouts.actions.add')} position="top">
                            <button
                                onClick={handleAddOpen}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
                                <Plus className="h-4 w-4" /> {t('sms.optouts.actions.add')}
                            </button>
                        </SimpleTooltip>
                    </div>
                }
            />

            {/* Add Opt-out Modal */}
            {addModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold mb-4">{t('sms.optouts.addTitle')}</h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    {t('sms.optouts.fields.phoneNumber')}
                                </label>
                                <input
                                    type="text"
                                    value={newPhoneNumber}
                                    onChange={(e) => setNewPhoneNumber(e.target.value)}
                                    placeholder="+15551234567"
                                    className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    {t('sms.optouts.fields.reason')}
                                </label>
                                <select
                                    value={newReason}
                                    onChange={(e) => setNewReason(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                >
                                    <option value="STOP">STOP</option>
                                    <option value="UNSUBSCRIBE">UNSUBSCRIBE</option>
                                    <option value="BLOCKED">BLOCKED</option>
                                    <option value="BOUNCED">BOUNCED</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setAddModalOpen(false)}
                                className="px-4 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleAddSubmit}
                                className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                            >
                                {t('sms.optouts.actions.add')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, optout: null })}
                onConfirm={confirmDelete}
                title={t('sms.optouts.deleteTitle')}
                message={`${t('sms.optouts.messages.confirmDelete')} (${deleteModal.optout?.phoneNumber})`}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
                type="danger"
            />
        </div>
    );
}
